import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { useEffect, useState } from "react";
import Joyride, { CallBackProps } from "react-joyride";
import { onboardingSteps } from "./dashboardConstants";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "@packages/store/hooks";
import { Button } from "@core/button";
import { useNavigate } from "react-router-dom";
import { getCurrentTeacher } from "@packages/store/userSlice";
import { editPreferences } from "@packages/api/madlenAPI";
import { useTheme } from "@packages/hooks/useTheme";

const DashboardMainOnboarding = () => {
  const { t } = useTranslation();
  const [runTour, setRunTour] = useState(false);
  const navigate = useNavigate();
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);
  const isMobile = useMobile();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const [viewCount, setViewCount] = useLocalStorage(
    "dashboard-tour-view-count",
    0,
  );
  const proDate = useAppSelector(
    (state) => state.user.teacher?.preferences?.teacherProDate,
  );
  const hasSeenTour = useAppSelector(
    (state) => state.user.teacher?.preferences?.didCompleteOnboarding,
  );
  const teacherId = useAppSelector((state) => state.user.teacher?.id);
  const teacherPreferences = useAppSelector(
    (state) => state.user.teacher?.preferences,
  );
  const isEdlinkUser = useAppSelector(
    (state) => !!state.user.teacher?.edlinkPersonId,
  );

  useEffect(() => {
    // Show tour if user hasn't seen it and isn't skipping all tours
    const shouldShowTour =
      !hasSeenTour && !skipAllTours && !isMobile && viewCount < 3 && !!proDate;
    setRunTour(shouldShowTour);
  }, [hasSeenTour, skipAllTours, isMobile, viewCount, proDate]);

  useEffect(() => {
    if (runTour) {
      setViewCount((prev) => prev + 1);
    }
  }, [runTour]);

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, status } = data;
    if (action === "close") {
      setRunTour(false);
      return;
    }
    if (status === "finished" || status === "skipped") {
      setRunTour(false);
      if (teacherId) {
        await editPreferences({
          preferences: {
            ...teacherPreferences,
            didCompleteOnboarding: true,
          },
        });
      }
      dispatch(getCurrentTeacher());
    }
  };

  return (
    <Joyride
      steps={onboardingSteps(isEdlinkUser).map((step, i) => ({
        ...step,
        content:
          isEdlinkUser &&
          (i === 0 || i === onboardingSteps(isEdlinkUser).length - 1) ? (
            <>
              <div className="flex flex-col items-center text-center gap-4">
                {t(step.content)}
                <Button
                  onClick={() => {
                    setRunTour(false);
                    window.location.href =
                      "https://ed.link/sso/login?client_id=6d462dde-5ceb-4239-8aa1-0a2a3e36cca3&redirect_uri=https://api.madlen.io/api/edlink/oauth/callback&state=" +
                      teacherId +
                      "," +
                      window.location.origin +
                      "/profiles/me?callback=true";
                  }}
                  variant="solid"
                  bgColor="bg-primary-500"
                  textColor="text-white"
                  size="sm"
                  rounded="lg"
                >
                  {t("onboarding.goToEdlink")}
                </Button>
              </div>
            </>
          ) : (
            t(step.content)
          ),
      }))}
      run={runTour}
      continuous
      styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
      callback={async (data) => {
        await handleJoyrideCallback(data);
      }}
      locale={{
        back: t("onboarding.back"),
        close: t("onboarding.close"),
        last: t("onboarding.finish"),
        next: t("onboarding.next"),
        skip: t("onboarding.skip"),
      }}
    />
  );
};

export default DashboardMainOnboarding;
